import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
    DollarOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/ui";

export const CurrencyShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "currency",
        id,
        meta: {
            fields: [
                "id",
                "currencyCode",
                "name",
                "symbol",
                "exchangeRate",
                "isBaseCurrency",
                "isActive",
                "createdAt",
                "updatedAt"
            ],
        }
    });

    const { data, isLoading } = queryResult;
    const record = data?.data;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageHeader
                title={`${record?.currencyCode || ''} - ${record?.name || 'Currency Details'}`}
                titleIcon={<DollarOutlined />}
                breadcrumbs={[
                    { title: "Currencies", href: "/currency" },
                    { title: record?.currencyCode || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/currency/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Symbol</div>
                        <div className="mini-stat-value text-primary">{record?.symbol || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Exchange Rate</div>
                        <div className="mini-stat-value">{record?.exchangeRate?.toFixed(4) || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Base Currency</div>
                        <Tag color={record?.isBaseCurrency ? "blue" : "default"}>
                            {record?.isBaseCurrency ? "Yes" : "No"}
                        </Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                            {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record?.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Currency Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Currency Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Currency Code:</span>
                                <span className="info-value font-semibold">{record?.currencyCode || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Name:</span>
                                <span className="info-value">{record?.name || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Symbol:</span>
                                <span className="info-value text-primary font-semibold">{record?.symbol || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Exchange Rate:</span>
                                <span className="info-value">{record?.exchangeRate?.toFixed(4) || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Base Currency:</span>
                                <Tag color={record?.isBaseCurrency ? "blue" : "default"}>
                                    {record?.isBaseCurrency ? "Yes" : "No"}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={record?.isActive ? "success" : "default"}>
                                    {record?.isActive ? "Active" : "Inactive"}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            {record?.updatedAt && (
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
